import Head from 'next/head'
import { buttonClassName } from '../components/ui/button'
import { HeroSection } from '../components/ui/galaxy-interactive-hero-section'
import { SiteFooter, SiteHeader } from '../components/ui/site-chrome'

const services = [
  {
    eyebrow: 'Fractional CTO',
    title: 'Technical leadership without the full-time executive overhead',
    copy:
      'For founders and teams that need a senior operator to translate business goals into architecture, hiring plans, delivery discipline, and sharper product decisions.',
    bullets: ['Technology roadmap', 'Team coaching', 'Architecture review', 'Vendor and AI tool selection'],
  },
  {
    eyebrow: 'AI Strategy',
    title: 'Use AI where it creates leverage, not theater',
    copy:
      'I help companies identify the workflows, data, and product surfaces where AI can reduce cost, improve speed, or create a defensible customer experience.',
    bullets: ['AI opportunity maps', 'Workflow automation', 'LLM product design', 'Governance and risk review'],
  },
  {
    eyebrow: 'Complex Systems',
    title: 'Find the bottlenecks hiding inside platforms and teams',
    copy:
      'When growth stalls, the issue is often buried in performance, integrations, process, or unclear ownership. I diagnose the circuit and show the highest-ROI fixes.',
    bullets: ['Platform audits', 'Performance triage', 'Operational mapping', 'Revenue-impact recommendations'],
  },
]

const workshops = [
  {
    title: 'AI Operator Workshop',
    format: 'Corporate teams',
    copy:
      'A practical session for startup and mid-market teams that need to understand AI tools, providers, workflows, and implementation risks without drowning in hype.',
  },
  {
    title: 'Build With AI Sprint',
    format: 'Hands-on city events',
    copy:
      'A fast-paced workshop where participants move from database schema and product idea to UI, backend behavior, and a working prototype using modern AI builders.',
  },
  {
    title: 'Founder CTO Lab',
    format: 'Small-group coaching',
    copy:
      'A guided environment for non-technical founders to learn how to scope, validate, hire, and lead technical work with better mental models and less guesswork.',
  },
]

const proof = [
  '17+ years across e-commerce, startups, real estate technology, cloud systems, and product delivery.',
  'Hands-on full-stack background across C#, Go, Python, TypeScript, React, Next.js, Node.js, GraphQL, Dart, AWS, GCP, Azure, Firebase, Supabase, and Digital Ocean.',
  'Experience translating between founders, engineering teams, vendors, and executive stakeholders so technology decisions stay tied to business outcomes.',
]

const process = [
  {
    step: '01',
    title: 'Map the circuit',
    copy:
      'We identify the people, platforms, processes, data, and handoffs that turn strategy into customer value.',
  },
  {
    step: '02',
    title: 'Find the voltage leaks',
    copy:
      'We isolate the bottlenecks: slow systems, weak workflows, misaligned teams, unrealistic roadmaps, AI risk, or vendor drag.',
  },
  {
    step: '03',
    title: 'Rewire for flow',
    copy:
      'We fix the highest-leverage parts first, coach the team through the change, and leave behind systems they can actually own.',
  },
]

const packages = [
  {
    name: 'Circuit Audit',
    price: 'From $25k',
    copy:
      'A focused diagnostic for leaders who need clarity before funding another roadmap, vendor, rebuild, or AI initiative.',
  },
  {
    name: 'Fractional CTO Partnership',
    price: 'Monthly or project-based',
    copy:
      'Hands-on leadership for product, engineering, architecture, AI adoption, team rhythm, and founder decision-making.',
  },
  {
    name: 'Workshops',
    price: 'Public and private cohorts',
    copy:
      'Practical AI and product-building workshops for companies, founders, and city-based cohorts ready to learn by shipping.',
  },
]

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Head>
        <title>Zadro | Fractional CTO & AI Strategy for Startups and Enterprise Teams</title>
        <meta
          name="description"
          content="Zadro helps founders, startups, and enterprise teams adopt AI correctly, fix complex technology bottlenecks, and build scale-ready systems with fractional CTO leadership."
        />
        <link rel="canonical" href="https://www.zadro.tech/" />
        <meta property="og:title" content="Zadro | Fractional CTO & AI Strategy" />
        <meta
          property="og:description"
          content="Technical leadership, AI strategy, workshops, and complex systems diagnosis for companies that need better wiring before more scale."
        />
        <meta property="og:url" content="https://www.zadro.tech/" />
        <meta property="og:site_name" content="Zadro" />
      </Head>

      <SiteHeader />

      <main>
        <HeroSection />

        <section id="services" className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="max-w-3xl">
            <p className="section-kicker">Services</p>
            <h2 className="section-title">Technical leadership for companies that need traction, clarity, and leverage.</h2>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {services.map((service) => (
              <article key={service.title} className="rounded-[1.5rem] border border-slate-800 bg-slate-900/80 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.24)]">
                <p className="font-mono text-sm font-semibold uppercase tracking-[0.18em] text-sky-300">{service.eyebrow}</p>
                <h3 className="mt-4 text-2xl font-bold text-white">{service.title}</h3>
                <p className="mt-4 leading-7 text-slate-300">{service.copy}</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-300">
                  {service.bullets.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-slate-800 bg-slate-900/60">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="section-kicker">Use Case</p>
              <h2 className="section-title">PopMart-style platform diagnosis</h2>
              <p className="mt-5 leading-7 text-slate-300">
                A global brand can have massive demand and still lose value through a slow or poorly
                wired digital experience. The job is to translate technical performance into business
                impact, then prioritize fixes that protect conversion, customer trust, and future product
                experiences.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="metric-card">
                <span>01</span>
                <strong>Performance leak</strong>
                <p>Find where speed, architecture, or integration drag blocks revenue.</p>
              </div>
              <div className="metric-card">
                <span>02</span>
                <strong>Business model impact</strong>
                <p>Connect technical symptoms to conversion, CAC, AOV, LTV, and expansion.</p>
              </div>
              <div className="metric-card">
                <span>03</span>
                <strong>Actionable blueprint</strong>
                <p>Give leadership a clear fix sequence instead of an expensive generic rebuild.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="workshops" className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="section-kicker">Workshops</p>
              <h2 className="section-title">AI education that ends with working systems, not just slides.</h2>
            </div>
            <p className="max-w-md text-slate-300">
              Built for private teams, founders, and public city cohorts. Registration and event promotion
              can live directly on Zadro as the workshop calendar grows.
            </p>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {workshops.map((workshop) => (
              <article key={workshop.title} className="rounded-[1.5rem] border border-slate-800 bg-slate-900/80 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.24)]">
                <p className="text-sm font-semibold text-sky-300">{workshop.format}</p>
                <h3 className="mt-3 text-2xl font-bold text-white">{workshop.title}</h3>
                <p className="mt-4 leading-7 text-slate-300">{workshop.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="method" className="border-y border-slate-800 bg-slate-950">
          <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
            <p className="section-kicker">Method</p>
            <h2 className="section-title max-w-3xl">The Navigator and Electrician approach.</h2>
            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {process.map((item) => (
                <article key={item.step} className="rounded-[1.5rem] border border-slate-800 bg-slate-900/70 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.22)]">
                  <span className="font-mono text-sm font-bold text-sky-300">{item.step}</span>
                  <h3 className="mt-4 text-2xl font-bold text-white">{item.title}</h3>
                  <p className="mt-4 leading-7 text-slate-300">{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="section-kicker">Trust</p>
            <h2 className="section-title">Built by an operator who can speak both founder and engineer.</h2>
          </div>
          <div className="space-y-4">
            {proof.map((item) => (
              <p key={item} className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 leading-7 text-slate-300">
                {item}
              </p>
            ))}
          </div>
        </section>

        <section className="border-y border-slate-800 bg-slate-900/60">
          <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
            <p className="section-kicker">Engagements</p>
            <h2 className="section-title max-w-3xl">Start with the level of clarity your company needs now.</h2>
            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {packages.map((item) => (
                <article key={item.name} className="rounded-[1.5rem] border border-slate-800 bg-slate-950 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.24)]">
                  <h3 className="text-2xl font-bold text-white">{item.name}</h3>
                  <p className="mt-3 text-lg font-semibold text-sky-300">{item.price}</p>
                  <p className="mt-4 leading-7 text-slate-300">{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="rounded-[1.75rem] border border-sky-300/30 bg-sky-300 p-8 text-slate-950 shadow-[0_28px_90px_rgba(56,189,248,0.18)] sm:p-10">
            <p className="font-mono text-sm font-bold uppercase tracking-[0.2em]">Next step</p>
            <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <h2 className="text-4xl font-black leading-tight sm:text-5xl">
                  Let&apos;s find the wiring that is slowing the system down.
                </h2>
                <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-800">
                  Bring a messy roadmap, an AI idea, a team that needs direction, or a platform that
                  is leaking revenue. We will turn it into a clear technical and business plan.
                </p>
              </div>
              <a
                href="/book"
                className={buttonClassName({ size: 'lg', variant: 'inverse' })}
              >
                Schedule a circuit audit
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}
